import { get, intersection, isEmpty, set } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { useScreenActionPermissions } from "shared/hooks/usePermissions";
import {
  BUILD_GENERAL_COST,
  BUILD_GENERAL_COSTS,
} from "utils/api/graphql/mutations/costs";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  CONTACT_POINT_SYSTEM,
  INITIAL_VALUES,
  INSURANCE_PLAN_PARAMS_RULES,
  MODULES,
  SUBMODULES,
} from "utils/constants";
import { getMinDeltaStart } from "utils/helpers/array";
import { generateSQLCondition } from "utils/helpers/graphql";
import View from "./View";

const buildInsurancePlanFilter = (project, processId, issuanceDate) => {
  const issuanceDateDifference = moment(issuanceDate)
    .startOf("day")
    .diff(moment().startOf("day"), "days");

  const adherentAge = moment(get(project, "contact.user.birthDate")).startOf(
    "year",
  );

  const age = moment().diff(adherentAge, "years");

  const [startKey, endKey] = project.ria
    ? [
        INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
        INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
      ]
    : [
        INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
        INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
      ];
  const socialRegime = get(project, "contact.socialRegime");

  const profession = get(project, "contact.profession");

  const zipCode = get(
    get(project, "contact.telecoms", []).find(
      ({ system }) => system === CONTACT_POINT_SYSTEM.ZIP_CODE,
    ),
    "value",
    "",
  ).toString();

  const department = zipCode.substring(0, 2);

  const sqlConditions = [
    generateSQLCondition(
      startKey,
      "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
      issuanceDateDifference,
    ),
    generateSQLCondition(
      endKey,
      `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
        moment(issuanceDate).year() === moment().year()
          ? "OR CAST(r.value AS NUMERIC) = 0"
          : ""
      }`,
      issuanceDateDifference,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
      "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
      age,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
      "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
      age,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES,
      `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
      `'${socialRegime}'`,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
      `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
      `'${profession}'`,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS,
      `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
      `'${department}'`,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES,
      `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
      `'${zipCode}'`,
    ),
    generateSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
      `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
      `'${processId}'`,
    ),
  ];

  return {
    rules: sqlConditions.filter(Boolean),
    custom: {
      AND: [
        {
          OR: [
            { startDate: null },
            {
              startDate: issuanceDate,
              operation: "<=",
            },
          ],
        },
        {
          OR: [
            { endDate: null },
            {
              endDate: issuanceDate,
              operation: ">=",
            },
          ],
        },
      ],
    },
  };
};

const List = ({ project, onNext, onBack, processId }) => {
  const actions = useScreenActionPermissions({
    module: MODULES.PROJECT,
    submodule: SUBMODULES.BROKER_DETAIL,
  });
  const { id } = useParams();
  const take = 5;
  const [nextLoading, setNextLoading] = useState(false);
  const [skip, setSkip] = useState(INITIAL_VALUES.skip);
  const [filter, setFilter] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedGeneralCosts, { loading }] = useLazyQuery(GENERAL_COSTS, {
    onCompleted: ({ generalCosts: { data } }) => {
      setSelectedProducts(omitDeep(data, "__typename"));
    },
  });
  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [getInsurancePlans] = useLazyQuery(INSURANCE_PLANS);

  // Unblocks View loading={isEmpty(filter)} even when build fails.
  const applyProductsFilter = useCallback(
    (costIds = []) => {
      setFilter({
        skip: 0,
        take,
        where: {
          project: { id },
        },
        gt: { cost: +get(project, "fields.budget.min") - 1 },
        lt: { cost: +get(project, "fields.budget.max") + 1 },
        isIn: { id: costIds },
      });
    },
    [id, project, take],
  );

  const handleBuildGeneralCostsCompleted = useCallback(
    ({ buildGeneralCosts: builtCosts }) => {
      const selectedInsurancePlans =
        intersection(
          builtCosts?.map(({ insurancePlan }) => insurancePlan?.id),
          get(project, "fields.selectedToCompare", []),
        ) || [];

      selectedGeneralCosts({
        variables: {
          where: { project: { id } },
          isIn: {
            insurancePlan: {
              id: selectedInsurancePlans,
            },
          },
        },
      });
      applyProductsFilter(builtCosts?.map(({ id: costId }) => costId) || []);
    },
    [applyProductsFilter, id, project, selectedGeneralCosts],
  );

  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);

  const runBuildGeneralCosts = useCallback(
    (issuanceDate) => {
      buildGeneralCosts({
        variables: {
          data: {
            project: { id },
            insurancePlanFilter: buildInsurancePlanFilter(
              project,
              processId,
              issuanceDate,
            ),
          },
        },
        onCompleted: handleBuildGeneralCostsCompleted,
        onError: () => applyProductsFilter([]),
      });
    },
    [
      applyProductsFilter,
      buildGeneralCosts,
      handleBuildGeneralCostsCompleted,
      id,
      processId,
      project,
    ],
  );

  const continueAfterInsurancePlans = useCallback(
    (insurancePlans) => {
      const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
      const deltaStart = getMinDeltaStart({
        insurancePlans,
        minValue: 1,
        key: "DELTA_START",
      });
      const deltaS = project.ria ? deltaRia : deltaStart;
      const correctedIssuanceDate = moment()
        .add(deltaS, "days")
        .isAfter(project?.contract?.issuanceDate)
        ? moment().add(deltaS, "days").toDate()
        : project?.contract?.issuanceDate;

      const shouldUpdateIssuanceDate =
        project.ria &&
        moment(correctedIssuanceDate).isAfter(
          moment(get(project, "contract.issuanceDate")),
          "day",
        );

      if (shouldUpdateIssuanceDate) {
        updateProject({
          variables: {
            where: { id },
            data: {
              contract: {
                id: get(project, "contract.id"),
                issuanceDate: correctedIssuanceDate,
              },
            },
          },
          onCompleted: () => runBuildGeneralCosts(correctedIssuanceDate),
          onError: () => runBuildGeneralCosts(correctedIssuanceDate),
        });
        return;
      }

      runBuildGeneralCosts(correctedIssuanceDate);
    },
    [id, project, runBuildGeneralCosts, updateProject],
  );

  const onAdd = (product) => setSelectedProducts((prev) => [...prev, product]);

  const onRemove = (product) =>
    setSelectedProducts((prev) => prev.filter(({ id }) => id !== product.id));

  const onChangeCommission = ({ commission, insurancePlan }) => {
    buildGeneralCost({
      variables: {
        data: {
          project: { id },
          insurancePlan,
          commission,
        },
      },
    });
  };
  const onFilter = ({ min, max, needs }) => {
    updateProject({
      variables: {
        where: { id },
        data: { fields: { ...project.fields, budget: { min, max }, needs } },
      },
      onCompleted: () => {
        set(project, "fields.needs", needs);
        buildGeneralCosts({
          variables: {
            data: {
              project: { id },
              insurancePlanFilter: buildInsurancePlanFilter(
                project,
                processId,
                get(project, "contract.issuanceDate"),
              ),
            },
          },
          onCompleted: () =>
            setFilter((prev) => ({
              ...prev,
              where: {
                ...prev.where,
                project: {
                  ...prev.where.project,
                  fields: {
                    ...prev.where.project.fields,
                    needs,
                  },
                },
              },
              gt: { cost: min - 1 },
              lt: { cost: max + 1 },
            })),
        });
      },
    });
  };

  const onSubmit = ({ fields }) => {
    setNextLoading(true);
    onNext({
      payload: {
        fields,
      },
      onCompleted: () => setNextLoading(false),
      onError: () => setNextLoading(false),
    });
  };

  useEffect(() => {
    if (project.locked) {
      selectedGeneralCosts({
        variables: {
          where: { project: { id } },
          isIn: {
            insurancePlan: { id: get(project, "fields.selectedToCompare", []) },
          },
        },
      });
      return setFilter({
        skip: INITIAL_VALUES.skip,
        take,
        where: { project: { id } },
      });
    }

    const fallbackIssuanceDate = get(project, "contract.issuanceDate");

    // Prefer .then over onCompleted: Apollo often skips onCompleted on cache hits,
    // which is why the 2nd visit on a long-lived online SPA stuck on Loading while
    // localhost (cold cache / HMR) always looked fine.
    getInsurancePlans({ variables: { where: {} } })
      .then((result) => {
        if (result?.error) {
          runBuildGeneralCosts(fallbackIssuanceDate);
          return;
        }
        continueAfterInsurancePlans(
          get(result, "data.insurancePlans.data", []),
        );
      })
      .catch(() => runBuildGeneralCosts(fallbackIssuanceDate));
  }, []);

  return (
    <View
      initialFields={project.fields}
      initialNeeds={project.fields.needs}
      expired={project?.expired}
      onNext={onNext}
      filter={filter}
      take={take}
      onBack={onBack}
      onAdd={onAdd}
      onRemove={onRemove}
      selectedProducts={selectedProducts}
      onChangeCommission={onChangeCommission}
      onFilter={onFilter}
      onLoadMore={setSkip}
      loading={isEmpty(filter)}
      actions={actions}
      onSubmit={onSubmit}
      nextLoading={nextLoading}
      processId={processId}
    />
  );
};

export default List;
